import * as React from 'react';

// Utils
import { isFeatureEnabled } from '@shopee-user/business-customization-logic';
import { Feature } from '@shopee-user/feature-toggle';
import { forwardOrAppendAuthParamsIfNotExist } from '@shopee-user/authentication-utils';
import { loginByPasswordURL } from '@shopee-user/url';
import { isSupportThirdpartyOAuth } from '@tob/shared/thirdparty-auth/utils';

// Components
import ThirdpartyLoginMethods from '@tob/shared/thirdparty-auth/features/login/ThirdpartyLoginMethods';
import Logo from '../../../Logo';
import { Form } from '@shopee/use-form-hook';
import { InputLoginByOtpAuthKey } from '@shopee-user/shared-customization-ui';
import LoginTermsOfService from '../../../LoginTermsOfService';
import { ButtonLogin } from '../../../Button';
import { Link } from 'react-router-dom';
import AltLoginLink from '../../../AltLoginLink';

// Hooks & HoC
import { useTranslation } from '@shopee-user/platform';
import { Layout, useResponsiveLayout } from '@shopee-user/layout-responsive';

// Types
import { LoginByOtpOneCredentialOptionalRequireAuthKeyProps } from '@shopee-user/business-customization-types';

import styles from './style.scss';
import { useLoginByOtpOneCredentialFlowEvent } from '@tob/pages/login-by-otp-one-credential-optional/flow-event';
import { ThirdpartyBindingEntryPoint } from '@tob/shared/thirdparty-auth/service/constants';

function _Content({
  ...props
}: LoginByOtpOneCredentialOptionalRequireAuthKeyProps) {
  const I18n = useTranslation();
  const {
    state: { isButtonSubmitDisabled },
    form,
    onSubmitForm,
    onThirdpartyLoginSuccess,
    onKeyDown,
  } = props;
  const {
    fields: { authKey: authKeyField },
  } = form;

  const { layout } = useResponsiveLayout();
  const { trackEvent } = useLoginByOtpOneCredentialFlowEvent();

  const MobileAltLoginLink = layout === Layout.Mobile && <AltLoginLink />;

  const MobileLogo = layout === Layout.Mobile && (
    <div className={styles.logoContainer}>
      <Logo className={styles.logo} />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      {MobileLogo}
      <div className={styles.formContainer}>
        <Form onEnterKeyDown={onSubmitForm}>
          <div className={styles.container}>
            <InputLoginByOtpAuthKey
              pristine={authKeyField.pristine}
              value={authKeyField.value}
              onChange={authKeyField.onChange}
              onFocus={authKeyField.onFocus}
              onBlur={authKeyField.onBlur}
              clear={authKeyField.clear}
              reset={authKeyField.reset}
              active={authKeyField.active}
              errors={authKeyField.errors.map((key: string) => I18n.t(key))}
            />
            <div className={styles.buttonWrapper}>
              <ButtonLogin
                onClick={onSubmitForm}
                disabled={isButtonSubmitDisabled}
              />
            </div>
            {isFeatureEnabled(Feature.LoginByPasswordEntrance) && (
              <div className={styles.linkWrapper}>
                <Link
                  to={forwardOrAppendAuthParamsIfNotExist(
                    loginByPasswordURL.getUrl()
                  )}
                  onKeyDown={onKeyDown}
                  className={styles.link}
                  onClick={() => {
                    trackEvent('onLoginLoginWithPasswordClick');
                  }}
                >
                  {I18n.t('label_auth_login_with_password')}
                </Link>
              </div>
            )}
            {isSupportThirdpartyOAuth() && (
              <ThirdpartyLoginMethods
                entryPoint={ThirdpartyBindingEntryPoint.LoginByOtpPage}
                className={styles.thirdpartyLoginMethods}
                dividerClassName={styles.thirdpartyLoginMethodsDivider}
                onLoginSuccess={onThirdpartyLoginSuccess}
              />
            )}
            {isFeatureEnabled(Feature.LoginTermsOfService) && (
              <LoginTermsOfService />
            )}
            {MobileAltLoginLink}
          </div>
        </Form>
      </div>
    </div>
  );
}

export const Content = _Content;
